import { createContext, type PropsWithChildren, useContext, useRef } from 'react';
import { useInRouterContext } from 'react-router-dom';
import { useStore } from 'zustand';
import {
  type Action,
  createTableStore,
  type State,
  type TableStoreProps,
  type TableStore as TableStoreType,
} from './UseTableStore';
import { useTableUrlSync } from './useTableUrlSync';

const TableStore = createContext<TableStoreType | null>(null);

type TableStoreProviderProps = PropsWithChildren<TableStoreProps>;

const TableUrlSync = ({ store, name }: { store: TableStoreType; name: string }) => {
  const inRouter = useInRouterContext();
  if (!inRouter) return null;
  return <TableUrlSyncInner name={name} store={store} />;
};

const TableUrlSyncInner = ({ store, name }: { store: TableStoreType; name: string }) => {
  useTableUrlSync(store, name);
  return null;
};

export const TableStoreProvider = ({ children, ...props }: TableStoreProviderProps) => {
  const storeRef = useRef<TableStoreType>(null);
  if (!storeRef.current) {
    storeRef.current = createTableStore(props);
  }
  return (
    <TableStore.Provider value={storeRef.current}>
      <TableUrlSync name={props.name} store={storeRef.current} />
      {children}
    </TableStore.Provider>
  );
};

export const useTableStore = <T,>(selector: (state: State & Action) => T): T => {
  const store = useContext(TableStore);
  if (!store) throw new Error('Missing TableStore.Provider in the tree');
  return useStore(store, selector);
};
